import * as React from 'react'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { MoreButton, OverflowDetector } from '@sales-center/components'
import { TruncatedText } from './TruncatedText'
import { getLocationCardStyles, useActions, useData } from './hooks'
import { AddServiceLocationDrawer } from '../AddServiceLocationForm'
import { ServiceLocationPopover } from '@user/components/EntityCards/atoms/ServiceLocationPopover'
import { LocationLink } from '../LocationInfoTag'

import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { DeleteDialog } from './DeleteDialog'
import { SxProps, useTheme } from '@mui/material/styles'

type BaseProps = {
  entityId: number
  entityType: ENTITY_TYPES
  location: LocationDTO
  /**
   * This callback fires after the card is toggled.
   * We use it upstream to resposition the LocationsPopover
   */
  onTransitionEnd?: () => void
  open: boolean
  toggleOpen: () => void
  header?: React.ReactNode
  footer?: React.ReactNode
  isEditable: boolean
  className?: SxProps
  style?: React.CSSProperties
  hidePopover?: boolean
}

type ReadonlyProps = BaseProps & {
  isEditable: false
}

type EditableProps = BaseProps & {
  isEditable: true
  entityBranchId: string
  canUpdateEntity: boolean
  lastServiceLocation?: boolean
  isAllServicesAssignedToLocation?: boolean
}

export type SmallLocationCardProps = ReadonlyProps | EditableProps

export const SmallLocationCard: React.FC<SmallLocationCardProps> = (props) => {
  const {
    location: { externalLinkId, locationCode, billingLocation, branchId, id },
    open,
    toggleOpen,
    onTransitionEnd = () => {},
    header,
    footer,
    className,
    style,
    hidePopover,
    isEditable,
  } = props

  const properties = useData(props)
  const { matched, menuOptions, formProps, deleteDialogProps } =
    useActions(props)

  const hasMoreFields = properties.length > 2

  const label = billingLocation ? 'Bill-to' : 'Service Location'

  const verb = open ? 'Less' : 'More'

  const showPopover = !billingLocation && !hidePopover
  const styles = getLocationCardStyles(useTheme())
  return (
    <>
      <Card elevation={2} sx={{ ...(className ?? styles.card) }} style={style}>
        {header}
        <Box display="flex" alignItems="center">
          <Typography sx={styles.bold}>{label}</Typography>

          {showPopover && (
            <Box display="flex" alignItems="center">
              <ServiceLocationPopover {...props} />
            </Box>
          )}

          {isEditable && menuOptions.length > 0 && (
            <MoreButton
              className={styles.moreBtn}
              options={menuOptions}
              dataTestId="billToMoreBtn"
            />
          )}
        </Box>

        {matched && (
          <LocationLink
            externalLocationId={externalLinkId ?? ''}
            externalLocationCode={locationCode ?? ''}
            billingLocation={billingLocation}
            icon
          />
        )}

        <OverflowDetector
          open={open}
          closedHeight={42}
          onTransitionEnd={onTransitionEnd}
        >
          {properties.map((p, i) => (
            <TruncatedText key={i} truncated={!open}>
              {p}
            </TruncatedText>
          ))}
        </OverflowDetector>

        {hasMoreFields && (
          <Typography
            sx={styles.expandLink}
            onClick={toggleOpen}
            data-test-id={`location-card-view-${verb}-${id}`}
          >
            View {verb} Information
          </Typography>
        )}
        {footer}
      </Card>
      <>
        <AddServiceLocationDrawer
          {...props}
          {...formProps}
          isBillingLocation={billingLocation}
          entityBranchId={branchId?.toString() ?? ''}
        />

        <DeleteDialog {...deleteDialogProps} matched={matched} />
      </>
    </>
  )
}
SmallLocationCard.displayName = 'SmallLocationCard'
